import * as XLSX from 'xlsx';
import { fetchData } from '../../utils/apiHelpers';
import { getAll } from '../../api';
import { getTaskCycleGroupById } from '../../api/task-cycle-groupAPI';
import axiosInstance from '../../api/axiosInstance';
import { getAssetByAssetNumber } from '../../api/assetAPI';
import { getSupplierByName } from '../../api/supplierAPI';
import { getAssetMeterByAssetAndMeter } from '../../api/asset-meterAPI';

export const parseFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      resolve(json);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};

export const validateRow = async (
  row,
  fields,
  entity,
  lookupCache = {},
  uniqueCache = {}
) => {
  const errors = {};
  const resolvedLookups = {};
  let pendingGroupCreation;
  let blockImport = false;
  const xorGroups = {};

  for (const field of fields) {
    const value = row[field.key];
    const isEmpty = value === undefined || value === null || value === '';

    const registerXorValue = () => {
      if (!field.xorGroup) return;
      const group = xorGroups[field.xorGroup] || {
        fields: [],
        noneMessage: field.xorMessages?.none,
        multipleMessage: field.xorMessages?.multiple,
      };
      const finalValue = row[field.key];
      const hasValue = !(finalValue === undefined || finalValue === null || finalValue === '');
      const hasError = Boolean(errors[field.key]);
      group.fields.push({ key: field.key, hasValue: hasValue && !hasError });
      xorGroups[field.xorGroup] = group;
    };

    if (field.required && isEmpty) {
      errors[field.key] = 'Required';
    }

    if (isEmpty) {
      registerXorValue();
      continue;
    }

    if (
      typeof field.maxLength === 'number' &&
      typeof value === 'string' &&
      value.length > field.maxLength
    ) {
      errors[field.key] = `Must be at most ${field.maxLength} characters`;
    }

    switch (field.type) {
      case 'number':
        if (isNaN(Number(value))) errors[field.key] = 'Must be a number';
        break;
      case 'date': {
        let parsedDate;

        // 1. Try DD/MM/YYYY string pattern
        if (typeof value === 'string') {
          const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          const match = value.match(datePattern);

          if (match) {
            const [_, day, month, year] = match.map(Number);
            const candidate = new Date(year, month - 1, day);

            if (
              candidate.getFullYear() === year &&
              candidate.getMonth() === month - 1 &&
              candidate.getDate() === day
            ) {
              parsedDate = candidate;
            } else {
              errors[field.key] = 'Invalid DD/MM/YYYY date';
              break;
            }
          }
        }

        // 2. Try Excel date serial number
        if (!parsedDate && typeof value === 'number') {
          // Excel serial date to JS Date (Excel epoch starts 1899-12-30)
          parsedDate = new Date(Math.round((value - 25569) * 86400 * 1000));
        }

        // 3. Try native Date object
        if (!parsedDate && value instanceof Date && !isNaN(value.getTime())) {
          parsedDate = value;
        }

        // 4. Final fallback
        if (!parsedDate || isNaN(parsedDate.getTime())) {
          errors[field.key] = 'Date must be in DD/MM/YYYY format or Excel date number';
          break;
        }

        // Normalize to YYYY-MM-DD
        const yyyy = parsedDate.getFullYear();
        const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(parsedDate.getDate()).padStart(2, '0');
        row[field.key] = `${yyyy}-${mm}-${dd}`;

        break;
      }

      case 'datetime': {
        let parsedDate;

        // 1. Try "DD/MM/YYYY HH:mm" or "DD/MM/YYYY HH:mm:ss"
        if (typeof value === 'string') {
          const dateTimePattern =
            /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

          const match = value.match(dateTimePattern);

          if (match) {
            const [
              _,
              dayStr,
              monthStr,
              yearStr,
              hourStr,
              minuteStr,
              secondStr,
            ] = match;

            const day = Number(dayStr);
            const month = Number(monthStr);
            const year = Number(yearStr);
            const hour = hourStr != null ? Number(hourStr) : 0;
            const minute = minuteStr != null ? Number(minuteStr) : 0;
            const second = secondStr != null ? Number(secondStr) : 0;

            const candidate = new Date(year, month - 1, day, hour, minute, second);

            // Validate that the date and time components survived the constructor
            if (
              candidate.getFullYear() === year &&
              candidate.getMonth() === month - 1 &&
              candidate.getDate() === day &&
              candidate.getHours() === hour &&
              candidate.getMinutes() === minute &&
              candidate.getSeconds() === second
            ) {
              parsedDate = candidate;
            } else {
              errors[field.key] =
                'Invalid DD/MM/YYYY HH:mm or DD/MM/YYYY HH:mm:ss datetime';
              break;
            }
          }
        }

        // 2. Try Excel serial datetime (number, fractional part is time)
        if (!parsedDate && typeof value === 'number') {
          // Excel epoch starts 1899-12-30, 25569 is days between 1899-12-30 and 1970-01-01
          parsedDate = new Date(Math.round((value - 25569) * 86400 * 1000));
        }

        // 3. Try native Date object
        if (!parsedDate && value instanceof Date && !isNaN(value.getTime())) {
          parsedDate = value;
        }

        // 4. Final fallback
        if (!parsedDate || isNaN(parsedDate.getTime())) {
          errors[field.key] =
            'Datetime must be DD/MM/YYYY HH:mm or an Excel datetime number';
          break;
        }

        // ✅ Normalise to "YYYY-MM-DD HH:mm:ss"
        const yyyy = parsedDate.getFullYear();
        const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(parsedDate.getDate()).padStart(2, '0');
        const HH = String(parsedDate.getHours()).padStart(2, '0');
        const MM = String(parsedDate.getMinutes()).padStart(2, '0');
        const SS = String(parsedDate.getSeconds()).padStart(2, '0');

        row[field.key] = `${yyyy}-${mm}-${dd} ${HH}:${MM}:${SS}`;

        break;
      }

      case 'lookup': {
        if (field.key === 'assetMeterId') {
          const numericValue = Number(value);
          if (!Number.isNaN(numericValue)) {
            row[field.key] = numericValue;
            break;
          }
        }

        if (field.api && field.nameField) {
          const specialLookups = {
            assets: async (value) => {
              // Requires special case for assets so large data set is not returned
              const asset = await getAssetByAssetNumber(value);
              return asset ? [asset] : [];
            },
            suppliers: async (value) => {
              // Requires special case for supplier so large data set is not returned
              const supplier = await getSupplierByName(value);
              return supplier ? [supplier] : [];
            },
          };
          let records = lookupCache[field.api];
          let match;

          try {
            if (specialLookups[field.api]) {
              const results = await specialLookups[field.api](value);
              match = results.find(
                (r) =>
                  String(r[field.nameField]).toLowerCase() ===
                  String(value).toLowerCase()
              );

              if (
                !match &&
                Array.isArray(results) &&
                results.length === 1 &&
                results[0]?.id &&
                (results[0][field.nameField] === undefined || results[0][field.nameField] === null)
              ) {
                // Some lookup endpoints (e.g. suppliers/by-name) only return the id. If we
                // already scoped the lookup by the provided value, accept the single
                // returned record even though it lacks the expected name field.
                match = results[0];
              }
            } else {
              if (!records) {
                records = await fetchData(getAll, { apiName: field.api });
                if (field.api === 'suppliers') {
                  lookupCache[field.api] = records.data;
                } else {
                  lookupCache[field.api] = records;
                }
              }

              if (field.key === 'taskId') {
                const assetIdForTaskLookup = row.assetId || row.asset_id;

                if (!assetIdForTaskLookup) {
                  errors[field.key] = 'Asset ID is required to resolve task';
                  break;
                }

                const filteredRecords = Array.isArray(records)
                  ? records.filter((r) => Number(r.assetId) === Number(assetIdForTaskLookup))
                  : [];

                match = filteredRecords.find(
                  (r) =>
                    String(r[field.nameField]).toLowerCase() === String(value).toLowerCase()
                );
              } else {
                match = Array.isArray(records)
                  ? records.find(
                    (r) =>
                      String(r[field.nameField]).toLowerCase() ===
                      String(value).toLowerCase()
                  )
                  : undefined;
              }
            }

            if (match) {
              if (Array.isArray(field.allowedNames)) {
                const candidateName = match[field.nameField];
                const isAllowed = field.allowedNames.some(
                  (allowed) =>
                    String(allowed).toLowerCase() === String(candidateName ?? '').toLowerCase()
                );
                if (!isAllowed) {
                  errors[field.key] = `Value must be one of: ${field.allowedNames.join(', ')}`;
                  break;
                }
              }
              if (field.key === 'assetMeterId') {
                try {
                  const resolvedAssetId = row.assetId || field.assetId;
                  if (!resolvedAssetId) {
                    errors[field.key] = 'Asset Id is required to resolve asset meter';
                    break;
                  }

                  const assetMeter = await getAssetMeterByAssetAndMeter(resolvedAssetId, match.id);
                  row[field.key] = assetMeter?.id ?? assetMeter?.[0]?.id;

                  if (!row[field.key]) errors[field.key] = 'Asset meter not linked to asset';
                } catch (err) {
                  console.error('Failed to resolve asset meter', err);
                  errors[field.key] = 'Asset meter lookup failed';
                }
              } else {
                row[field.key] = match.id;
              }

              resolvedLookups[field.key] = match;
            } else {
              if (field.key === 'groupId') {
                const manufacturerIdValue = Number(row.manufacturerId);
                const classIdValue = Number(row.classId);
                const hasManufacturerLookup =
                  !errors.manufacturerId && Number.isInteger(manufacturerIdValue) && manufacturerIdValue > 0;
                const hasClassLookup =
                  !errors.classId && Number.isInteger(classIdValue) && classIdValue > 0;

                if (hasManufacturerLookup && hasClassLookup) {
                  pendingGroupCreation = {
                    groupName: value,
                    manufacturerId: manufacturerIdValue,
                    classId: classIdValue,
                  };
                } else {
                  if (!hasManufacturerLookup && !errors.manufacturerId) {
                    errors.manufacturerId = 'Manufacturer must match an existing record';
                  }
                  if (!hasClassLookup && !errors.classId) {
                    errors.classId = 'Class must match an existing record';
                  }
                  errors[field.key] = 'Group not found';
                  blockImport = true;
                }
              } else {
                errors[field.key] = 'Invalid value';
              }
            }
          } catch (err) {
            errors[field.key] = 'Lookup failed';
          }
          break;
        }
        break;
      }

      default:
        break;
    }

    if (!errors[field.key] && field.unique) {
      const cacheKey = `${field.key}-${value}`;
      if (uniqueCache[cacheKey] === undefined) {
        try {
          const res = await axiosInstance.get(
            `${process.env.REACT_APP_API_URL}/${entity}/by-${field.key}/${encodeURIComponent(value)}`
          );

          // If the response has a result, it’s a duplicate
          if (res?.data) {
            uniqueCache[cacheKey] = false;
          } else {
            uniqueCache[cacheKey] = true;
          }
        } catch (err) {
          if (err.response && err.response.status === 404) {
            uniqueCache[cacheKey] = true; // Not found = unique
          } else {
            errors[field.key] = 'Unique check failed';
          }
        }
      }

      if (uniqueCache[cacheKey] === false) {
        errors[field.key] = `Duplicate ${field.key} found`;
      }
    }

    registerXorValue();

  }

  if (entity === 'workorders') {
    const resolvedTask = resolvedLookups.taskId;
    const taskIdValue = row.taskId || row.task_id;

    if (resolvedTask?.schedulingType === 'cycle' && Number.isInteger(Number(taskIdValue))) {
      const cycleGroupId = resolvedTask.cycleGroupId;

      if (cycleGroupId) {
        const cacheKey = `taskCycleGroup:${cycleGroupId}`;

        if (!(cacheKey in lookupCache)) {
          try {
            lookupCache[cacheKey] = await getTaskCycleGroupById(cycleGroupId);
          } catch (err) {
            console.error('Failed to fetch cycle task data', err);
            lookupCache[cacheKey] = null;
          }
        }

        const cycleGroup = lookupCache[cacheKey];

        if (Array.isArray(cycleGroup?.tasks)) {
          resolvedTask.taskCycleTasks = cycleGroup.tasks;
        }
      }

      const hasCycleGroupOrderValue = !(row.cycleGroupOrder === undefined || row.cycleGroupOrder === null || row.cycleGroupOrder === '');

      if (!hasCycleGroupOrderValue && !errors.cycleGroupOrder) {
        errors.cycleGroupOrder = 'cycleGroupOrder is required for cycle tasks';
      }

      const cycleGroupOrderNumber = Number(row.cycleGroupOrder);

      if (hasCycleGroupOrderValue && !errors.cycleGroupOrder) {
        const hasCycleTaskRows = Array.isArray(resolvedTask.taskCycleTasks) && resolvedTask.taskCycleTasks.length > 0;

        const matchesCycleOrder = hasCycleTaskRows
          ? resolvedTask.taskCycleTasks.some(
            (taskCycleTask) =>
              Number(taskCycleTask?.taskId) === Number(taskIdValue) &&
              Number(taskCycleTask?.order) === cycleGroupOrderNumber
          )
          : true;

        if (!Number.isFinite(cycleGroupOrderNumber)) {
          errors.cycleGroupOrder = 'cycleGroupOrder must be a number';
        } else if (hasCycleTaskRows && !matchesCycleOrder) {
          errors.cycleGroupOrder = 'cycleGroupOrder must match a task cycle order';
        }
      }
    }
  }

  if (entity === 'bill-of-materials') {
    const hasQuantityValue = !(
      row.quantity === undefined ||
      row.quantity === null ||
      row.quantity === ''
    );
    if (hasQuantityValue && !errors.quantity) {
      const quantityValue = Number(row.quantity);
      if (!Number.isFinite(quantityValue)) {
        errors.quantity = 'Quantity must be a number';
      } else if (!Number.isInteger(quantityValue) || quantityValue < 0) {
        errors.quantity = 'Quantity must be a whole number of 0 or more';
      } else {
        row.quantity = quantityValue;
      }
    }

    const hasOptionalValue = !(
      row.optional === undefined ||
      row.optional === null ||
      row.optional === ''
    );
    if (hasOptionalValue && !errors.optional) {
      const optionalValue = Number(row.optional);
      if (![0, 1].includes(optionalValue)) {
        errors.optional = 'Optional must be 0 or 1';
      } else {
        row.optional = optionalValue;
      }
    }
  }

  if (entity === 'assets') {
    const hasEndOfLifeValue = !(
      row.endOfLife === undefined || row.endOfLife === null || row.endOfLife === ''
    );
    const startReadingValue = Number(row.startReading ?? 0) || 0;
    const utilisationValue = Number(row.utilisation ?? 0) || 0;

    if (hasEndOfLifeValue) {
      const endOfLifeValue = Number(row.endOfLife);

      if (!Number.isFinite(endOfLifeValue)) {
        errors.endOfLife = errors.endOfLife || 'End of life must be a number';
      } else {
        if (endOfLifeValue === 0) {
          errors.endOfLife = 'End of life cannot be zero';
        }

        if (!errors.endOfLife && endOfLifeValue <= startReadingValue) {
          errors.endOfLife = 'End of life must be greater than start reading';
        }
      }
    } else if (!errors.assetStartDate && !errors.assetEndDate) {
      const startDate = row.assetStartDate ? new Date(row.assetStartDate) : null;
      const endDate = row.assetEndDate ? new Date(row.assetEndDate) : null;

      if (startDate instanceof Date && endDate instanceof Date && !isNaN(startDate) && !isNaN(endDate)) {
        const millisecondsPerYear = 1000 * 60 * 60 * 24 * 365.25;
        const lifeInYears = (endDate.getTime() - startDate.getTime()) / millisecondsPerYear;
        const calculatedEndOfLife = utilisationValue * lifeInYears + startReadingValue;

        if (Number.isFinite(calculatedEndOfLife)) {
          row.endOfLife = calculatedEndOfLife;
        }
      }
    }
  }

  Object.entries(xorGroups).forEach(([groupKey, group]) => {
    const providedCount = group.fields.filter((f) => f.hasValue).length;
    if (providedCount === 0) {
      const message = group.noneMessage || 'One of these fields is required';
      group.fields.forEach(({ key }) => {
        if (!errors[key]) {
          errors[key] = message;
        }
      });
    } else if (providedCount > 1) {
      const message = group.multipleMessage || 'Only one of these fields can be provided';
      group.fields.forEach(({ key }) => {
        errors[key] = errors[key] || message;
      });
    }
  });

  return { errors, pendingGroupCreation, blockImport };
};

export const validateRows = async (rows, fields, entity, onProgress) => {
  const lookupCache = {};
  const uniqueCache = {};
  const validated = [];
  let count = 0;
  for (const row of rows) {
    const { errors, pendingGroupCreation, blockImport } = await validateRow(
      row,
      fields,
      entity,
      lookupCache,
      uniqueCache
    );
    const resultRow = { ...row, _errors: errors };

    if (pendingGroupCreation && Object.keys(errors).length === 0) {
      resultRow._pendingGroupCreation = pendingGroupCreation;
    }

    if (blockImport) {
      resultRow._blockImport = true;
    }

    validated.push(resultRow);
    count += 1;
    if (onProgress) onProgress(count);
  }
  return validated;
};
